/**
 * Deterministic OIDC session-storage access.
 *
 * The OIDC library (oidc-client-ts under @uofu-uss/uss-oidc) stores the authenticated
 * user under a key of the form `oidc.user:<authority>:<client_id>`. Under reused browser
 * contexts (load-test runners, long-lived tabs, environment switches) MULTIPLE
 * `oidc.user:*` entries can exist at once. Naively picking the first key returned by
 * `Object.keys(sessionStorage)` is nondeterministic and can select a stale or
 * wrong-environment token, producing intermittent 401s from the WSO2 gateway.
 *
 * This module centralizes key selection with a deterministic policy:
 *   1. Prefer keys belonging to the current environment's client_id.
 *   2. Prefer entries whose token is not expired.
 *   3. Among remaining candidates, prefer the latest `expires_at` (freshest token).
 *   4. Prune expired duplicate entries so they cannot be selected later.
 *
 * NOTE: This module must stay dependency-light (no @uofu-uss/uss-oidc import) so it is
 * safe to import statically from code paths that bypass OIDC on localhost.
 */
import { getWSO2Config } from '../config/wso2Config';

export interface OidcUserData {
  access_token?: string;
  expires_at?: number | string;
  profile?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface OidcUserEntry {
  key: string;
  data: OidcUserData;
}

const OIDC_USER_KEY_PREFIX = 'oidc.user:';

function getAllOidcUserKeys(): string[] {
  return Object.keys(sessionStorage).filter(key => key.startsWith(OIDC_USER_KEY_PREFIX));
}

function parseEntry(key: string): OidcUserEntry | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') {
      return null;
    }
    return { key, data };
  } catch {
    return null;
  }
}

function isEntryExpired(entry: OidcUserEntry, nowMs: number): boolean {
  const expiresAt = Number(entry.data.expires_at);
  if (!Number.isFinite(expiresAt)) {
    // No usable expiry info - treat as not expired so we don't discard valid sessions.
    return false;
  }
  return expiresAt * 1000 <= nowMs;
}

function expiresAtOf(entry: OidcUserEntry): number {
  const expiresAt = Number(entry.data.expires_at);
  return Number.isFinite(expiresAt) ? expiresAt : 0;
}

/**
 * Selects the OIDC user entry for the current environment using a deterministic policy.
 * Also prunes expired duplicate entries when more than one candidate exists.
 *
 * @returns The selected entry, or undefined when no usable entry exists.
 */
export function selectOidcUserEntry(): OidcUserEntry | undefined {
  const keys = getAllOidcUserKeys();
  if (keys.length === 0) {
    return undefined;
  }

  // Prefer entries scoped to the current environment's client id.
  // Key format: oidc.user:<authority>:<client_id>
  const clientId = getWSO2Config().clientId;
  const scopedKeys = keys.filter(key => key.endsWith(`:${clientId}`));
  const candidateKeys = scopedKeys.length > 0 ? scopedKeys : keys;

  const entries = candidateKeys
    .map(parseEntry)
    .filter((entry): entry is OidcUserEntry => entry !== null);

  if (entries.length === 0) {
    return undefined;
  }

  const now = Date.now();
  const sorted = [...entries].sort((a, b) => {
    const aExpired = isEntryExpired(a, now);
    const bExpired = isEntryExpired(b, now);
    if (aExpired !== bExpired) {
      return aExpired ? 1 : -1; // unexpired entries first
    }
    return expiresAtOf(b) - expiresAtOf(a); // freshest token first
  });

  const selected = sorted[0];

  // Prune expired duplicates so stale entries cannot win a future selection.
  if (entries.length > 1) {
    for (const entry of sorted.slice(1)) {
      if (isEntryExpired(entry, now)) {
        console.warn(`Removing stale OIDC session entry: ${entry.key}`);
        sessionStorage.removeItem(entry.key);
      }
    }
  }

  return selected;
}

/**
 * @returns The storage key of the current environment's OIDC user, if any.
 */
export function getOidcUserStorageKey(): string | undefined {
  return selectOidcUserEntry()?.key;
}

/**
 * @returns The parsed OIDC user data for the current environment, or null.
 */
export function getOidcUserData(): OidcUserData | null {
  return selectOidcUserEntry()?.data ?? null;
}

/**
 * Checks whether a stored OIDC token is expired based on its `expires_at` claim.
 * Entries without usable expiry info are treated as not expired (the gateway/backend
 * still enforce real expiry server-side).
 */
export function isTokenDataExpired(data: OidcUserData | null): boolean {
  if (!data) {
    return false;
  }
  const expiresAt = Number(data.expires_at);
  if (!Number.isFinite(expiresAt)) {
    return false;
  }
  return expiresAt * 1000 <= Date.now();
}

